import React, { useCallback, useMemo, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';

import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { auth } from '@/features/auth/types';
import { LevelDetailPinBadge } from '@/features/guardian-player/components/level-detail';
import {
  BADGE_STATUS_COLORS,
  CHECKPOINT_STATUS_COLORS,
  LEVEL_BORDER_EDGE_WIDTH,
  LEVEL_BORDER_STEPS,
  LEVEL_BORDER_TIP_WIDTH,
  SESSION_DETAIL_STRINGS,
} from '@/features/guardian-player/components/player-session-details/constants';
import {
  buildGuardianPinPicture,
  buildGuardianSessionDetailBadgeSections,
  formatGuardianApiDate,
  getGuardianSessionAttemptStatus,
  getGuardianSessionBadgeKey,
  type GuardianSessionDetailLevelSection,
  isGuardianSessionOverallPinName,
} from '@/features/guardian-player/utils';
import { BottomSideBorder } from '@/features/guardian/components/bottom-side-border';
import { buildPinUrl } from '@/features/player-session/utils';
import { CHECKPOINT_LEADER_DOTS } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import { formatProgressBadgeTitle } from '@/features/player/utils/progress-title';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { DATE_FORMATS } from '@/utils/date-formats';

type SessionDetailLevelCardProps = {
  level: number;
  pins: GuardianSessionDetailLevelSection['pins'];
  attempts: GuardianSessionDetailLevelSection['attempts'];
  sessionDate?: string;
};

const strings = SESSION_DETAIL_STRINGS;

function getLevelColor(level: number) {
  return colors[`Level ${level}` as keyof typeof colors] ?? guardianColors['Main/Main'];
}

function CheckpointRow({ checkpoint }: { checkpoint: auth.GuardianPlayerSessionDetailAttempt }) {
  const styles = useStyles();
  const { scale } = useScaling();
  const status = getGuardianSessionAttemptStatus(checkpoint.attempts);

  return (
    <Stack direction="row" align="center" gap={scale(6)} style={styles.checkpointRow}>
      <Text variant="Font Inter/Subtitle/Regular" numberOfLines={1} style={styles.checkpointTitle}>
        {checkpoint.checkpointName}
      </Text>
      <Text
        numberOfLines={1}
        ellipsizeMode="clip"
        variant="Font Inter/Subtitle/Regular"
        selectable={false}
        style={styles.dots}>
        {CHECKPOINT_LEADER_DOTS}
      </Text>
      <Text
        variant="Font Inter/Subtitle/Regular"
        style={[
          styles.checkpointStatus,
          { color: CHECKPOINT_STATUS_COLORS[status] },
          status === 'incomplete' ? styles.incompleteStatus : null,
        ]}>
        {strings.checkpointStatus[status]}
      </Text>
    </Stack>
  );
}

function BadgeSection({
  badgeName,
  level,
  pin,
  attempts,
  showDivider,
}: {
  badgeName: string;
  level: number;
  pin?: GuardianSessionDetailLevelSection['pins'][number];
  attempts: auth.GuardianPlayerSessionDetailAttempt[];
  showDivider: boolean;
}) {
  const styles = useStyles();
  const { scale } = useScaling();
  const badgeStatus = pin ? 'earned' : 'working';
  const pinPicture = buildGuardianPinPicture(level, badgeName, badgeStatus);
  const pinImageUrl = buildPinUrl(pinPicture);
  const displayBadgeName = formatProgressBadgeTitle(badgeName);

  const isHaveAttempts = attempts.length > 0;

  return (
    <Stack>
      <Stack direction="row" align="center" gap={scale(12)} marginY={scale(12)}>
        <LevelDetailPinBadge pinImageUrl={pinImageUrl} size={scale(32)} />
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
          numberOfLines={1}
          style={styles.badgeTitle}>
          {displayBadgeName}
        </Text>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
          style={[styles.badgeStatus, { color: BADGE_STATUS_COLORS[badgeStatus] }]}>
          {strings.badgeStatus[badgeStatus]}
        </Text>
      </Stack>

      <Stack gap={scale(12)}>
        {attempts.map((checkpoint, index) => (
          <CheckpointRow
            key={`${checkpoint.checkpointName}-${checkpoint.attempts.join('-')}-${index}`}
            checkpoint={checkpoint}
          />
        ))}
      </Stack>

      {showDivider ? (
        <Stack
          height={1}
          backgroundColor={guardianColors['3']}
          marginTop={isHaveAttempts ? scale(12) : 0}
        />
      ) : null}
    </Stack>
  );
}

export default function SessionDetailLevelCard({
  level,
  pins,
  attempts,
  sessionDate,
}: SessionDetailLevelCardProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const levelColor = getLevelColor(level);
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });
  const overallPin = useMemo(
    () => pins.find(pin => isGuardianSessionOverallPinName(pin.name)),
    [pins],
  );
  const overallPinImageUrl = useMemo(() => buildPinUrl(`LV${level}_Overall.png`), [level]);
  const earnedRawDate = overallPin?.dateEarned ?? overallPin?.earnedDate ?? overallPin?.date;
  const earnedDate = formatGuardianApiDate(
    earnedRawDate ?? sessionDate,
    DATE_FORMATS.TEXT,
    earnedRawDate ?? sessionDate ?? '',
  );
  const badges = useMemo(
    () => buildGuardianSessionDetailBadgeSections({ level, pins, attempts }),
    [attempts, level, pins],
  );

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setMeasuredSize(previousSize =>
      previousSize.width === width && previousSize.height === height
        ? previousSize
        : { width, height },
    );
  }, []);

  const renderLevelHeader = () => (
    <Text
      variant="Font Guardian App/Body/Body 16 Bold"
      style={[styles.levelTitle, { color: levelColor }]}>
      {strings.levelTitle.replace('{level}', String(level))}
    </Text>
  );

  const renderOverallPinHeader = () => (
    <>
      <Stack direction="row" align="center" gap={scale({ phone: 8, tablet: 16 })}>
        <Image
          source={overallPinImageUrl ? { uri: overallPinImageUrl } : undefined}
          width={scale(60)}
          height={scale(60)}
          resizeMode="contain"
        />

        <Stack flex={1} align="flex-start" justify="center">
          <Text
            variant="Font Guardian App/Body/Body 16 Bold"
            style={[styles.levelTitle, { color: levelColor }]}>
            {strings.levelTitle.replace('{level}', String(level))}
          </Text>
          <Stack
            direction="row"
            align="center"
            justify="center"
            gap={scale(8)}
            marginTop={scale(4)}
            style={styles.earnedPill}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
              style={styles.earnedPillText}>
              {strings.badgeStatus.earned}
            </Text>
            <SvgIcon name="IcStar2" size={scale(12)} color={guardianColors['Main/Main']} />
            {earnedDate ? (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
                style={styles.earnedPillText}>
                {earnedDate}
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
      <Stack
        height={1}
        backgroundColor={guardianColors['3']}
        style={styles.edgeToEdgeDivider}
        marginTop={scale(12)}
      />
    </>
  );

  return (
    <Stack backgroundColor={colors['0']} onLayout={handleLayout} style={styles.card}>
      {overallPin ? renderOverallPinHeader() : renderLevelHeader()}

      <Stack>
        {badges.map((badge, index) => (
          <BadgeSection
            key={getGuardianSessionBadgeKey(level, badge.name)}
            badgeName={badge.name}
            level={level}
            pin={badge.pin}
            attempts={badge.attempts}
            showDivider={index < badges.length - 1}
          />
        ))}
      </Stack>

      <BottomSideBorder
        color={levelColor}
        edgeWidth={LEVEL_BORDER_EDGE_WIDTH}
        height={measuredSize.height}
        radius={scale(12)}
        side="left"
        steps={LEVEL_BORDER_STEPS}
        tipWidth={LEVEL_BORDER_TIP_WIDTH}
        width={measuredSize.width}
      />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(16),
    paddingVertical: scale(12),
  },
  levelTitle: {
    flexShrink: 1,
  },
  edgeToEdgeDivider: {
    marginHorizontal: scale(-16),
  },
  earnedPill: {
    borderRadius: scale(30),
    borderCurve: 'continuous',
    paddingHorizontal: scale(16),
    paddingVertical: scale(8),
    backgroundColor: guardianColors['Main/10'],
    shadowColor: guardianColors.Blue,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.16,
    shadowRadius: 4,
    elevation: 2,
  },
  earnedPillText: {
    color: guardianColors['Main/Main'],
  },
  badgeTitle: {
    color: guardianColors['10'],
    flex: 1,
  },
  badgeStatus: {
    flexShrink: 0,
  },
  checkpointRow: { overflow: 'hidden' },
  checkpointTitle: {
    color: guardianColors['6'],
    flexShrink: 1,
    maxWidth: '55%',
  },
  checkpointStatus: {
    flexShrink: 0,
  },
  incompleteStatus: {
    fontStyle: 'italic',
  },
  dots: {
    flex: 1,
    marginBottom: scale(2),
    color: guardianColors['6'],
  },
}));
